// Utility helpers to compute preview URLs and detect media types from original URLs

/**
 * Extracts lowercase file extension from a URL (without query/hash), e.g. ".gif"
 * Returns empty string if not found
 */
export function getFileExtensionFromUrl(url) {
    if (!url || typeof url !== 'string') return '';
    try {
        const u = new URL(url);
        const pathname = u.pathname || '';
        const last = pathname.split('/').pop() || '';
        const dotIdx = last.lastIndexOf('.');
        if (dotIdx === -1) return '';
        return last.slice(dotIdx).toLowerCase();
    } catch {
        // Fallback naive parsing
        const sanitized = url.split('#')[0].split('?')[0];
        const last = sanitized.split('/').pop() || '';
        const dotIdx = last.lastIndexOf('.');
        if (dotIdx === -1) return '';
        return last.slice(dotIdx).toLowerCase();
    }
}

export function isGifUrl(url) {
    return getFileExtensionFromUrl(url) === '.gif';
}

export function isVideoUrl(url) {
    const ext = getFileExtensionFromUrl(url);
    return ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'].includes(ext);
}

/**
 * Builds a preview URL based on original object URL.
 * Pattern example:
 *   original: https://.../telesun/1/91b317-580.gif
 *   preview:  https://.../telesun/1/preview/78x101/91b317-580.webp
 */
export function buildPreviewUrlFromOriginal(originalUrl, size = '78x101') {
    if (!originalUrl) return null;
    try {
        const u = new URL(originalUrl);
        const parts = (u.pathname || '').split('/').filter(Boolean);
        if (parts.length === 0) return null;
        const filename = parts[parts.length - 1];
        const baseName = filename.includes('.') ? filename.substring(0, filename.lastIndexOf('.')) : filename;
        const baseParts = parts.slice(0, -1); // everything except file
        const previewParts = [...baseParts, 'preview', size, `${baseName}.webp`];
        const newPath = '/' + previewParts.join('/');
        return `${u.origin}${newPath}`;
    } catch {
        return null;
    }
}

/**
 * Returns best thumbnail URL for a postcard-like object.
 * Prefers provided preview field, otherwise derives from original image_url.
 */
export function getThumbnailUrl(postcard, fallbackOriginalUrl, size = '78x101') {
    if (postcard && postcard.preview_78x101_url && size === '78x101') {
        return postcard.preview_78x101_url;
    }
    const original = (postcard && postcard.image_url) || fallbackOriginalUrl;
    if (!original) return null;
    // For videos, we typically do not have a webp preview; return original
    if (isVideoUrl(original)) return original;
    return buildPreviewUrlFromOriginal(original, size) || original;
}

